import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

import {
   Button,
   Hierarchy,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   ArrowNarrowIcon,
   MailIcon
} from '@flowwai-frontend/design-system/icons'
import { ClientConfigType } from '@flowwai-frontend/bps-officer-web/iam'
import { isLargeDesktop } from '@flowwai-frontend/shared/utils/web'
import { isCaptchaEnableInLoginPage } from '@flowwai-frontend/bps-officer-web/common/data-access'

import {
   loginMachineEvents,
   loginMachineTags
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'
import { validateEmail } from '../../utils/validationUtils'

import TCAndPrivacyPolicy from './TCAndPrivacyPolicy'
import * as Styles from './styles'
import './index.css'
import PrivacyPolicy from './PrivacyPolicy'
import { Config } from '@flowwai-frontend/shared/constants'

interface LoginWithPhoneNumberProps {
   loginMachineSend: LoginMachineSend
   loginMachineState: LoginMachineState
   clientConfig: ClientConfigType
}

const LoginWithPhoneNumberV2 = (
   props: LoginWithPhoneNumberProps
): ReactElement => {
   const { loginMachineSend, loginMachineState, clientConfig } = props

   const {
      context,
      context: { email: defaultEmail }
   } = loginMachineState

   const [email, setEmail] = useState<string>(defaultEmail)

   const [isValid, setIsValid] = useState(defaultEmail ? true : false)

   const [captchaValue, setCaptchaValue] = useState<string | null>(null)

   const [errorMessage, setErrorMessage] = useState('')
   const [isTAndCAccepted, setIsTAndCAccepted] = useState(true)

   const isCaptchaSelected = !captchaValue && isCaptchaEnableInLoginPage

   const isLoginButtonDisabled =
      !isValid || !isTAndCAccepted || isCaptchaSelected

   const customT = useCustomT('auth:login')

   const { theme } = useAppTheme()
   const { loginWithEmail: loginCardTheme } = theme.auth.login

   const errorTransitionClassName = 'login-textfield-error-transition '

   const isLargeDevice = isLargeDesktop()

   useEffect(() => {
      if (email) {
         validate(email)
      }
   }, [email])

   const onBlurEmail = (): void => {
      validate(email || '')
   }

   const unsetError = (): void => {
      setIsValid(true)
      setError('')
   }

   const setError = (errorText: string): void => {
      setErrorMessage(errorText)
   }

   const validate = (value: string): void => {
      const validationObject = validateEmail(value)

      if (validationObject.isValid) {
         unsetError()
         setIsValid(true)
      } else {
         setError(validationObject.errorMessage || '')
         setIsValid(false)
      }
   }

   const onChangeEmail = (value: string): void => {
      setEmail(value)

      validate(value)
      loginMachineSend({
         type: loginMachineEvents.onChangeValue
      })
   }

   const onClickSendOTP = (event: React.SyntheticEvent): void => {
      event.preventDefault()
      if (isValid) {
         if (!email) return
         loginMachineSend({
            type: loginMachineEvents.requestEmailOtp,
            email: email,
            isAcceptedTnc: isTAndCAccepted,
            captchaToken: isCaptchaEnableInLoginPage ? captchaValue : null
         })
      }
   }

   const renderLeftIcon = (): ReactElement => (
      <div className='mr-8px'>
         <MailIcon />
      </div>
   )

   const renderHeader = (): ReactElement => (
      <>
         <span
            className={cn(
               Styles.loginTextStyles,
               loginCardTheme.loginTextColor,
               {
                  'mb-1': !isLargeDevice
               }
            )}
         >
            {customT('officerPortalLoginText')}
         </span>
         <span
            className={cn(
               Styles.loginDescriptionStyles,
               loginCardTheme.loginDescriptionTextColor
            )}
         >
            {customT('officerPortalLoginDescription')}
         </span>
      </>
   )

   const onCaptchaValueChange = (value: string | null): void => {
      setCaptchaValue(value)
   }

   return (
      <form
         className={cn(
            Styles.getLoginContainerStyles(isLargeDevice),
            loginCardTheme.defaultBorderColor,
            loginCardTheme.cardBgColor
         )}
         onSubmit={onClickSendOTP}
      >
         {renderHeader()}

         <TextField
            size='Small'
            onBlur={onBlurEmail}
            errorMessage={errorMessage || context.errorMessage}
            value={email || ''}
            type='email'
            onChange={onChangeEmail}
            label={customT('officerPortalEmailLabel')}
            placeholder={customT('officerPortalEmailPlaceholder')}
            leftElement={renderLeftIcon}
            errorTransitionClassName={errorTransitionClassName}
            autoFocus
         />
         {/* <TCAndPrivacyPolicy
            privacyPolicyUrl={clientConfig.privacyPolicyAndTC.privacyPolicyUrl}
            termsAndConditionsUrl={
               clientConfig.privacyPolicyAndTC.termsAndConditionsUrl
            }
            isTAndCAccepted={isTAndCAccepted}
            setIsTAndCAccepted={setIsTAndCAccepted}
            className='mt-6'
         /> */}
         {isCaptchaEnableInLoginPage ? (
            <div className={cn(Styles.captchaContainer)}>
               <ReCAPTCHA
                  sitekey={'6LdirvgqAAAAANwXI_ab69zP4mC1Hul25NkjKBLF'}
                  onChange={onCaptchaValueChange}
               />
            </div>
         ) : null}

         <Button
            type='submit'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            size={'Large'}
            isDisabled={isLoginButtonDisabled}
            rightIcon={() => <ArrowNarrowIcon />}
            className='mt-6'
            isLoading={loginMachineState.hasTag(
               loginMachineTags.requestOtpLoading
            )}
         >
            {customT('getOtp')}
         </Button>
         <PrivacyPolicy
            privacyPolicyUrl={clientConfig.privacyPolicyAndTC.privacyPolicyUrl}
            className='mt-4'
         />
      </form>
   )
}

export default observer(LoginWithPhoneNumberV2)
